<template>
  <div style="height: 100%; position: relative;">
    <DashboardFilterChip :chip-color="chipColor" :chip-text="chipText"></DashboardFilterChip>
    <v-card style="height: 100%;">
      <v-card-text>
        <v-row class="mt-3">
          <v-col cols="12" align="center">
            <h1>Submissions</h1>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="selectedClientList"
              :items="clientsList"
              item-text="clientName"
              item-value="clientZohoId"
              label="Clients"
              multiple
              chips
              outlined
              hide-details
              dense
              :disabled="disableButtons"
              @change="selectedClientsChange"
            >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0 && selectedClientList.length !== clientsList.length">
                <span>{{ item.clientName }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedClientList.length !== clientsList.length"
                class="grey--text text-caption"
              >
                (+{{ selectedClientList.length - 1 }} others)
              </span>
              <v-chip v-if="index === 1 && selectedClientList.length === clientsList.length">
                <span>All Clients</span>
              </v-chip>
            </template>
            <template v-slot:prepend-item>
              <v-btn
                color="primary"
                text
                block
                @click="selectAllClientsHandler"
              >
                {{ selectAllClientsStatus ? 'Unselect All' : 'Select All' }}
              </v-btn>
            </template>
          </v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="px-0" align="center">
            <v-btn
              depressed
              :color="yearButtonColor"
              :disabled="disableButtons"
              @click="changeFilterButtonColor"
            >
              Year
            </v-btn>
            <v-btn
              depressed
              :color="monthButtonColor"
              :disabled="disableButtons"
              @click="changeFilterButtonColor"
            >
              Month
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6" md="3" align="center">
            <YearsProgressiveListDropDown
              v-if="filterToggle === YEAR"
              :year="yearFilter"
              :dense="true"
              :hideDetails="true"
              :disabled="disableButtons"
              @yearChange="yearFilterChangeHandler"
            ></YearsProgressiveListDropDown>
            <MonthPicker
              v-if="filterToggle === MONTH"
              label="Month"
              :year="yearFilter"
              :month="monthFilter"
              :dense="true"
              :hideDetails="true"
              :disabled="disableButtons"
              @yearChange="(newValue) => this.yearFilter = parseInt(newValue)"
              @monthChange="(newValue) => this.monthFilter = parseInt(newValue)"
              @periodChange="periodFilterChangeHandler"
            ></MonthPicker>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-row>
              <v-col cols="6" sm="6" class="container d-flex" align="center">
                <h2 class="justify-content-center align-self-center" style="width:100%;">S/I: {{ parseFloat(submissionsForInterview).toFixed(2) }}</h2>
              </v-col>
              <v-col cols="6" sm="6" class="container d-flex" align="center">
                <h2 class="justify-content-center align-self-center" style="width:100%;">S/P: {{ parseFloat(submissionsForPlacement).toFixed(2) }}</h2>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="mt-3">
            <v-row justify="end" class="mb-2">
              <v-col cols="auto">
                <ButtonCustomIcon
                  icon="cloud_download"
                  color="primary"
                  text="Excel"
                  :disabled="disableButtons || exportRows.length === 0"
                  @click="handleExportToExcel"
                />
              </v-col>
            </v-row>
            <v-data-table
              :headers="mainTableHeaders"
              :items="tableToDisplay"
              :calculate-widths="true"
              :items-per-page="5"
              style="height: 400px; overflow: auto;"
            >
              <template v-slot:item.details="{ item }">
                <v-btn
                  v-if="item.recruiterZohoId"
                  class="mx-1 my-1 hide-on-print"
                  color="primary"
                  outlined
                  :disabled="disableButtons"
                >
                  <v-icon color="primary" @click="showDetailsHandler(item)">info</v-icon>
                </v-btn>
              </template>
              <template slot="body.append">
                <tr>
                    <th class="text-right">TOTAL</th>
                    <th class="text-center">{{ totalSubmissions }}</th>
                    <th class="text-center">{{ totalInterviews }}</th>
                    <th class="text-center">{{ totalPlacements }}</th>
                    <th></th>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="end" class="pt-0">
            <v-btn
              v-if="!showFetchingDataGIF"
              color="primary"
              outlined
              class="ml-5"
              @click="refreshTile()"
              style="float: left;"
              >
                <v-icon>refresh</v-icon>
            </v-btn>
            <i
              v-if="showFetchingDataGIF"
              class="mr-2"
            >
              Fetching Data
            </i>
            <v-progress-circular
              v-if="showFetchingDataGIF"
              :size="30"
              :width="7"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <i
              v-if="lastRefreshTime"
              class="ml-10 mr-5"
            >
              Last Update: {{ lastRefreshTime }}
            </i>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <SubmissionsDetails
      ref="SubmissionsDetails"
    ></SubmissionsDetails>
    <MessageBox
      ref="messageBox"
      message="There is no data to show yet"
      color="warning"
      closeTextColor="white"
    ></MessageBox>
  </div>
</template>

<script>
import SubmissionsDetails from './SubmmissionsDetails.vue'
import { MessageBox } from '@/components/shared/messages'
import DashboardFilterChip from '@/components/dashboard/DashboardFilterChip'
import { mapActions } from 'vuex'
import { isJSONString } from '@/lib/miscellaneous'
import { YearsProgressiveListDropDown } from '@/components/years'
import { MonthPicker } from '@/components/shared/datePickers'
import { getLastDayOfMonth } from '@/lib/dates'
import { getLocalTimeString } from '@/lib/display'
import { ButtonCustomIcon } from '@/components/buttons'
import excel from '@/lib/export/excel'
import { buildSubmissionsExport } from './submissionsExportHelpers'

export default {
  components: {
    DashboardFilterChip,
    SubmissionsDetails,
    MessageBox,
    YearsProgressiveListDropDown,
    MonthPicker,
    ButtonCustomIcon
  },
  props: {
    chipText: {
      type: String,
      default: 'All Business Entity'
    },
    chipColor: {
      type: String,
      default: 'primary'
    }
  },
  data () {
    return {
      recruitersPerformanceDetails: [],
      YEAR: 1,
      MONTH: 2,
      filterToggle: 1,
      monthButtonColor: '',
      yearButtonColor: 'primary',
      selectedClientList: [],
      selectAllClientsStatus: null,
      yearFilter: new Date().getFullYear(),
      monthFilter: (new Date().getMonth() + 1) < 10
        ? '0'.concat((new Date().getMonth() + 1).toString())
        : (new Date().getMonth() + 1).toString(),
      lastRefreshTime: null,
      showFetchingDataGIF: false,
      disableButtons: false
    }
  },
  computed: {
    mainTableHeaders: {
      get () {
        let headers = [
          { text: 'Recruiter', value: 'recruiterName' },
          { text: 'Submissions', value: 'submissionsCount', align: 'center' },
          { text: 'Interviews', value: 'interviewsCount', align: 'center' },
          { text: 'Placements', value: 'placementsCount', align: 'center' },
          { text: 'Details', value: 'details', width: '70px', align: 'center', sortable: false }
        ]
        return headers
      }
    },
    tableToDisplay: {
      get () {
        let table = []
        if (this.recruitersPerformanceDetails && this.recruitersPerformanceDetails.length > 0) {
          table = this.recruitersPerformanceDetails.filter(row => this.selectedClientList.includes(row.clientZohoId))
          table = table.reduce((acc, row) => {
            if (this.filterToggle === this.YEAR) {
              if (acc) {
                let searchResult = acc.find(i => i.recruiterZohoId === row.recruiterZohoId)
                if (searchResult) {
                  searchResult['submissionsCount'] = searchResult['submissionsCount'] + row.submissionsCount
                  searchResult['interviewsCount'] = searchResult['interviewsCount'] + row.interviewsCount
                  searchResult['placementsCount'] = searchResult['placementsCount'] + row.placementsCount
                } else {
                  acc.push({
                    recruiterZohoId: row.recruiterZohoId,
                    recruiterName: row.recruiterName,
                    submissionsCount: row.submissionsCount,
                    interviewsCount: row.interviewsCount,
                    placementsCount: row.placementsCount
                  })
                }
              } else {
                acc = [
                  {
                    recruiterZohoId: row.recruiterZohoId,
                    recruiterName: row.recruiterName,
                    submissionsCount: row.submissionsCount,
                    interviewsCount: row.interviewsCount,
                    placementsCount: row.placementsCount
                  }
                ]
              }
            } else {
              let monthFirstDate = new Date(this.yearFilter, this.monthFilter - 1, 1)
              let monthLastDate = new Date(getLastDayOfMonth(monthFirstDate.toISOString()))
              monthLastDate.setDate(monthLastDate.getDate() + 1)
              let tempRowData = {}

              tempRowData.submissionsList = []
              let submissionsList = row.submissionsList && row.submissionsList.length > 0
                ? row.submissionsList.filter(
                  s => s.associated_Candidates &&
                    s.associated_Candidates.length > 0 &&
                    new Date(s.associated_Candidates[0].last_Activity_Time) >= monthFirstDate &&
                    new Date(s.associated_Candidates[0].last_Activity_Time) <= monthLastDate) : []
              if (submissionsList) {
                if (Array.isArray(submissionsList)) {
                  tempRowData.submissionsList.push(...submissionsList)
                } else {
                  tempRowData.submissionsList.push(submissionsList)
                }
              }
              tempRowData.submissionsCount = tempRowData.submissionsList ? tempRowData.submissionsList.length : 0

              tempRowData.interviewsList = []
              let interviewsList = row.interviewsList && row.interviewsList.length
                ? row.interviewsList.find(i => new Date(i.start_DateTime) >= monthFirstDate && new Date(i.end_DateTime) <= monthLastDate) : []
              if (interviewsList) {
                if (Array.isArray(interviewsList)) {
                  tempRowData.interviewsList.push(...interviewsList)
                } else {
                  tempRowData.interviewsList.push(interviewsList)
                }
              }
              tempRowData.interviewsCount = tempRowData.interviewsList ? tempRowData.interviewsList.length : 0

              tempRowData.placementsList = []
              let placementsList = row.placementsList && row.placementsList.length > 0
                ? row.placementsList.find(
                  p => p.associated_Candidates &&
                    p.associated_Candidates.length > 0 &&
                    new Date(p.associated_Candidates[0].last_Activity_Time) >= monthFirstDate &&
                    new Date(p.associated_Candidates[0].last_Activity_Time) <= monthLastDate) : []
              if (placementsList) {
                if (Array.isArray(placementsList)) {
                  tempRowData.placementsList.push(...placementsList)
                } else {
                  tempRowData.placementsList.push(placementsList)
                }
              }
              tempRowData.placementsCount = tempRowData.placementsList ? tempRowData.placementsList.length : 0

              if (acc) {
                let searchResult = acc.find(i => i.recruiterZohoId === row.recruiterZohoId)
                if (searchResult) {
                  searchResult['submissionsCount'] = searchResult['submissionsCount'] + tempRowData.submissionsCount
                  searchResult['interviewsCount'] = searchResult['interviewsCount'] + tempRowData.interviewsCount
                  searchResult['placementsCount'] = searchResult['placementsCount'] + tempRowData.placementsCount
                } else {
                  acc.push({
                    recruiterZohoId: row.recruiterZohoId,
                    recruiterName: row.recruiterName,
                    submissionsCount: tempRowData.submissionsCount,
                    interviewsCount: tempRowData.interviewsCount,
                    placementsCount: tempRowData.placementsCount
                  })
                }
              } else {
                acc = [
                  {
                    recruiterZohoId: row.recruiterZohoId,
                    recruiterName: row.recruiterName,
                    submissionsCount: tempRowData.submissionsCount,
                    interviewsCount: tempRowData.interviewsCount,
                    placementsCount: tempRowData.placementsCount
                  }
                ]
              }
            }
            return acc
          }, [])
          table.sort((a, b) => (a.recruiterName > b.recruiterName) ? 1 : -1)
        }
        if (table.length < 10) {
          let missingRowsCount = 10 - table.length
          for (let i = 0; i < missingRowsCount; i++) {
            table.push({
              recruiterZohoId: null,
              recruiterName: null,
              submissionsCount: null,
              interviewsCount: null,
              placementsCount: null
            })
          }
        }
        return table
      }
    },
    exportRows () {
      return this.tableToDisplay.filter(row => row.recruiterName)
    },
    totalSubmissions () {
      let total = 0
      if (this.tableToDisplay && this.tableToDisplay.length > 0) {
        total = this.tableToDisplay.reduce((acc, row) => {
          return acc + row.submissionsCount
        }, 0)
      }
      return total
    },
    totalInterviews () {
      let total = 0
      if (this.tableToDisplay && this.tableToDisplay.length > 0) {
        total = this.tableToDisplay.reduce((acc, row) => {
          return acc + row.interviewsCount
        }, 0)
      }
      return total
    },
    totalPlacements () {
      let total = 0
      if (this.tableToDisplay && this.tableToDisplay.length > 0) {
        total = this.tableToDisplay.reduce((acc, row) => {
          return acc + row.placementsCount
        }, 0)
      }
      return total
    },
    submissionsForInterview () {
      return this.totalInterviews ? this.totalSubmissions / this.totalInterviews : 0
    },
    submissionsForPlacement () {
      return this.totalPlacements ? this.totalSubmissions / this.totalPlacements : 0
    },
    clientsList () {
      if (this.recruitersPerformanceDetails && this.recruitersPerformanceDetails.length > 0) {
        let clients = this.recruitersPerformanceDetails.reduce((acc, row) => {
          let searchResult = acc.find(client => client.clientZohoId === row.clientZohoId)
          if (!searchResult) {
            acc.push({ clientZohoId: row.clientZohoId, clientName: row.clientName })
          }
          return acc
        }, [])
        return clients.sort((a, b) => (a.clientName > b.clientName) ? 1 : -1)
      }
      return []
    }
  },
  watch: {
    clientsList () {
      this.selectedClientList = this.clientsList.map(client => { return client.clientZohoId })
      this.selectAllClientsStatus = true
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'getRecruitersPerformanceDetails'
    ]),
    handleExportToExcel () {
      if (!this.exportRows.length) {
        return
      }

      excel.exportArray2Excel(buildSubmissionsExport({
        items: this.exportRows,
        headers: this.mainTableHeaders,
        isMonthly: this.filterToggle === this.MONTH,
        year: this.yearFilter,
        month: this.monthFilter
      }))
    },
    changeFilterButtonColor () {
      this.monthButtonColor = this.monthButtonColor === 'primary' ? '' : 'primary'
      this.yearButtonColor = this.yearButtonColor === 'primary' ? '' : 'primary'
      this.filterToggle = this.monthButtonColor === 'primary' ? this.MONTH : this.YEAR
      if (this.yearFilter === new Date().getFullYear()) {
        this.monthFilter = new Date().getMonth() + 1
      }
    },
    showDetailsHandler (item) {
      let details = this.recruitersPerformanceDetails.filter(i => i.recruiterZohoId === item.recruiterZohoId && this.selectedClientList.includes(i.clientZohoId))
      // filter by month
      if (this.filterToggle === this.MONTH) {
        let monthFirstDate = new Date(this.yearFilter, this.monthFilter - 1, 1)
        let monthLastDate = new Date(getLastDayOfMonth(monthFirstDate.toISOString()))
        monthLastDate.setDate(monthLastDate.getDate() + 1)
        details = details.reduce((acc, row) => {
          let tempRowData = {}
          tempRowData.submissionsList = []
          let submissionsList = row.submissionsList && row.submissionsList.length > 0
            ? row.submissionsList.filter(
              s => s.associated_Candidates &&
                s.associated_Candidates.length > 0 &&
                new Date(s.associated_Candidates[0].last_Activity_Time) >= monthFirstDate &&
                new Date(s.associated_Candidates[0].last_Activity_Time) <= monthLastDate)
            : []
          if (submissionsList) {
            if (Array.isArray(submissionsList)) {
              tempRowData.submissionsList.push(...submissionsList)
            } else {
              tempRowData.submissionsList.push(submissionsList)
            }
          }
          tempRowData.submissionsCount = tempRowData.submissionsList ? tempRowData.submissionsList.length : 0

          tempRowData.interviewsList = []
          let interviewsList = row.interviewsList && row.interviewsList.length
            ? row.interviewsList.find(i => new Date(i.start_DateTime) >= monthFirstDate && new Date(i.end_DateTime) <= monthLastDate) : []
          if (interviewsList) {
            if (Array.isArray(interviewsList)) {
              tempRowData.interviewsList.push(...interviewsList)
            } else {
              tempRowData.interviewsList.push(interviewsList)
            }
          }
          tempRowData.interviewsCount = tempRowData.interviewsList ? tempRowData.interviewsList.length : 0

          tempRowData.placementsList = []
          let placementsList = row.placementsList && row.placementsList.length > 0
            ? row.placementsList.find(
              p => p.associated_Candidates &&
              p.associated_Candidates.length > 0 &&
              new Date(p.associated_Candidates[0].last_Activity_Time) >= monthFirstDate &&
              new Date(p.associated_Candidates[0].last_Activity_Time) <= monthLastDate)
            : []
          if (placementsList) {
            if (Array.isArray(placementsList)) {
              tempRowData.placementsList.push(...placementsList)
            } else {
              tempRowData.placementsList.push(placementsList)
            }
          }
          tempRowData.placementsCount = tempRowData.placementsList ? tempRowData.placementsList.length : 0
          acc.push({
            clientName: row.clientName,
            clientZohoId: row.clientZohoId,
            recruiterName: row.recruiterName,
            recruiterZohoId: row.recruiterZohoId,
            submissionsList: tempRowData.submissionsList,
            submissionsCount: tempRowData.submissionsCount,
            interviewsList: tempRowData.interviewsList,
            interviewsCount: tempRowData.interviewsCount,
            placementsList: tempRowData.placementsList,
            placementsCount: tempRowData.placementsCount
          })
          return acc
        }, [])
      }
      details.sort((a, b) => (a.clientName > b.clientName) ? 1 : -1)
      // check if there is something to show.
      let showDetailsModal = details.reduce((acc, row) => {
        if (row.interviewsCount > 0 || row.submissionsCount > 0 || row.placementsCount > 0) {
          acc = true
        }
        return acc
      }, false)
      if (showDetailsModal) {
        for (let row of details) {
          if (row.interviewsList && row.interviewsList.length > 0) {
            row.interviewsList.sort((a, b) => (new Date(a.start_DateTime) > new Date(b.start_DateTime)) ? -1 : 1)
          }
          if (row.submissionsList && row.submissionsList.length > 0) {
            row.submissionsList.sort((a, b) => (new Date(a.modified_Time) > new Date(b.modified_Time)) ? -1 : 1)
          }
          if (row.placementsList && row.placementsList.length > 0) {
            row.placementsList.sort((a, b) => (new Date(a.modified_Time) > new Date(b.modified_Time)) ? -1 : 1)
          }
        }
        this.$refs.SubmissionsDetails.showModal(item.recruiterName, details)
      } else {
        this.$refs.messageBox.show()
      }
    },
    selectAllClientsHandler () {
      if (this.selectAllClientsStatus) { // All are selected
        this.selectedClientList = []
        this.selectAllClientsStatus = false
      } else {
        this.selectedClientList = this.clientsList.map(client => { return client.clientZohoId })
        this.selectAllClientsStatus = true
      }
    },
    selectedClientsChange (newValue) {
      if (this.selectAllClientsStatus && newValue.length !== this.clientsList.length) {
        this.selectAllClientsStatus = false
      } else if (!this.selectAllClientsStatus && newValue.length === this.clientsList.length) {
        this.selectAllClientsStatus = true
      }
    },
    yearFilterChangeHandler (newValue) {
      if (this.yearFilter !== newValue) {
        this.refreshTile(newValue)
      }
      this.yearFilter = newValue
      this.monthFilter = 1
    },
    periodFilterChangeHandler () {
      if (this.yearFilter !== this.recruitersPerformanceDetails[0].year) {
        this.refreshTile(this.yearFilter)
      }
    },
    refreshTile (year = null) {
      if (!year) {
        year = new Date().getFullYear()
        this.yearFilter = year
      }
      this.showFetchingDataGIF = true
      this.disableButtons = true
      this.getRecruitersPerformanceDetails({ year: year }).then((data) => {
        if (data && data.length > 0) {
          let dateTimeNow = new Date()
          this.recruitersPerformanceDetails = data
          localStorage.setItem('dashboard:recruiters-performance-details', JSON.stringify(data))
          localStorage.setItem('dashboard:recruiters-performance-details-year-filter', JSON.stringify(year))
          localStorage.setItem('dashboard:recruiters-performance-details-last-refresh-time', JSON.stringify(dateTimeNow.toUTCString()))
          this.lastRefreshTime = getLocalTimeString(dateTimeNow)
        }
        this.showFetchingDataGIF = false
        this.disableButtons = false
      })
    }
  },
  created () {
    if (localStorage.getItem('dashboard:recruiters-performance-details') && isJSONString(localStorage.getItem('dashboard:recruiters-performance-details'))) {
      this.recruitersPerformanceDetails = JSON.parse(localStorage.getItem('dashboard:recruiters-performance-details'))
    }
    if (localStorage.getItem('dashboard:recruiters-performance-details-year-filter') && isJSONString(localStorage.getItem('dashboard:recruiters-performance-details-year-filter'))) {
      this.yearFilter = JSON.parse(localStorage.getItem('dashboard:recruiters-performance-details-year-filter'))
    }
    if (localStorage.getItem('dashboard:recruiters-performance-details-last-refresh-time') && isJSONString(localStorage.getItem('dashboard:recruiters-performance-details-last-refresh-time'))) {
      let lastRefreshTime = new Date(JSON.parse(localStorage.getItem('dashboard:recruiters-performance-details-last-refresh-time')))
      this.lastRefreshTime = getLocalTimeString(lastRefreshTime)
    }
    if (this.yearFilter === new Date().getFullYear()) {
      this.refreshTile(this.yearFilter)
    }
  }
}
</script>
